import React, { createContext, useState, useEffect, useContext } from 'react';
import frTranslations from '../locales/fr.js';
import enTranslations from '../locales/en.js';

const LanguageContext = createContext(null);

const translations = {
  fr: frTranslations,
  en: enTranslations
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Récupérer la langue depuis localStorage ou détecter depuis le navigateur
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && (savedLanguage === 'fr' || savedLanguage === 'en')) {
      return savedLanguage;
    }
    // Détecter la langue du navigateur
    const browserLang = navigator.language || navigator.userLanguage;
    return browserLang.startsWith('fr') ? 'fr' : 'en';
  });

  useEffect(() => {
    // Sauvegarder la langue dans localStorage
    localStorage.setItem('language', language);
    // Mettre à jour l'attribut lang du HTML
    document.documentElement.lang = language;
  }, [language]);

  const changeLanguage = (newLanguage) => {
    if (newLanguage === 'fr' || newLanguage === 'en') {
      setLanguage(newLanguage);
    }
  };

  const t = (key, params = {}) => {
    if (!key) return '';
    
    const keys = key.split('.');
    let value = translations[language];
    
    // Debug en développement
    if (process.env.NODE_ENV === 'development' && !value) {
      console.warn(`[i18n] Langue ${language} non trouvée dans translations`);
    }
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Si la traduction n'existe pas, retourner la clé
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[i18n] Traduction manquante: ${key} (langue: ${language})`);
        }
        return key;
      }
    }

    // Si c'est une string, remplacer les paramètres
    if (typeof value === 'string') {
      return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
        return params[paramKey] !== undefined ? params[paramKey] : match;
      });
    }

    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
